import { environment } from "src/environments/environment";
import { ApiConfigurationService, AppConfig } from "../core/services/configuration.service";
import { MsalLoaderService } from "./MsalLoader.service";
import { APP_INITIALIZER, FactoryProvider, InjectionToken } from "@angular/core";
import { getValueIfPropertyExists } from "../utils/helper";
import { GlobalCacheLoaderService } from "./GlobalCacheLoader.service";
import packageJson from "../../../package.json";

function appInitializerFactory(
  apiConfigurationService: ApiConfigurationService,
  msalLoaderService: MsalLoaderService,
  globalCacheLoaderService: GlobalCacheLoaderService) {
  return async () => {

    // Ensure the browser cache is compatible with current version of the code before proceeding
    ensureSafeBrowserCache();

    // Ensure the startup URL is safe before proceeding
    // A URL is not considered safe if it has a pathname other than '/'.
    if (!ensureSafeStartupUrl()) {
      return Promise.resolve();
    };

    /**
     * Proposed flow:
     * 1. Load MSAL module to handle authentication and acquire token for API resource
     * 2. Load bootstrap config from /api/config
     * 3. Acquire token for API resource
     * 4. Load app config from /api/config using the acquired token
     */
    const token = await msalLoaderService.loadMsalModule();

    let configLoaded = false;
    try {
      await Promise.all([
        token ? apiConfigurationService.loadAppConfig(token) : Promise.resolve(),
        getStartupParams()
      ]);
      configLoaded = token !== undefined;
    } catch (error) {
      console.error('Failed to load application configuration:', error);
      // Continue with default configuration
      configLoaded = false;
    }

    if (configLoaded) {
      populateFromAppConfig(apiConfigurationService.getAppConfig());
      await globalCacheLoaderService.loadGlobalCacheConfig();
    } else {
      console.error('Failed to load application configuration');
    }

    if (startupParams.destination) {
      await globalCacheLoaderService.loadGlobalCacheScript();
      await globalCacheLoaderService.loadGlobalCacheData();
    }

    removeSpinner();

    return Promise.resolve();
  };
}

function populateFromAppConfig(appConfig: AppConfig) {
  const hs = appConfig.hostSettings;
  hostConfig = {
    ebrUrl: getValueIfPropertyExists(environment, 'ebrUrl', hs.ebrUrl),
    ebrApiUrl:getValueIfPropertyExists(environment, 'ebrApiUrl', hs.ebrApiUrl),
    programHubUrlHost: getValueIfPropertyExists(environment, 'programHubUrlHost', hs.programHubUrlHost),
    mixpanel: hs.mixpanel,
  };

  globalErrorHandlingConfig = {
    logErrorToConsole: appConfig.globalErrorHandlingLogErrorToConsole
  };

  azureMonitorConfig = {
    connectionString: appConfig.azureMonitorConnectionString,
    diagnostics: {
      isDistributedTracingEnabled: appConfig.azureMonitorDiagnosticsIsDistributedTracingEnabled
    }
  };

  downloadSettings = appConfig.downloadSettings;

  // Read feature flags from API response if available, otherwise use defaults.
  // Most feature flags default to true (feature enablement), but banner flags default to false.
  // Backend can control all flags via the /api/v2/config endpoint's featureFlags property.
  const flags = appConfig.featureFlags ?? {};
  featureFlags = {
    enableTechnicalIssuesBanner: flags['enableTechnicalIssuesBanner'] ?? false,
    enableActionCenter: flags['enableActionCenter'] ?? true,
    enableAsiaPacificRegionQSR: flags['enableAsiaPacificRegionQSR'] ?? true,
    enableSolutionParticipationQsr: flags['enableSolutionParticipationQsr'] ?? true,
    enableSolutionParticipationFrs: flags['enableSolutionParticipationFrs'] ?? true,
    enableCostAssumptionsQSR: flags['enableCostAssumptionsQSR'] ?? true,
    enableTvdBrandGroupTabs: flags['enableTvdBrandGroupTabs'] ?? true,
    enableAsiaPacificSolPartInstitutional: flags['enableAsiaPacificSolPartInstitutional'] ?? true,
    enableAsiaPacificSolPartFRS: flags['enableAsiaPacificSolPartFRS'] ?? true,
    enableAsiaPacificSolPartQSR: flags['enableAsiaPacificSolPartQSR'] ?? true,
    enableLatinAmericaSolPartInstitutional: flags['enableLatinAmericaSolPartInstitutional'] ?? true,
    enableLatinAmericaSolPartFRS: flags['enableLatinAmericaSolPartFRS'] ?? true,
    enableLatinAmericaSolPartQSR: flags['enableLatinAmericaSolPartQSR'] ?? true ,
    enableTVDAndSPForIMEAInstitutional: flags['enableTVDAndSPForIMEAInstitutional'] ?? true,
    enableTVDForAPInstitutional: flags['enableTVDForAPInstitutional'] ?? true,
    enableTVDForLAInstitutional: flags['enableTVDForLAInstitutional'] ?? true,
    enableCurrencyAndUoMConversion: flags['enableCurrencyAndUoMConversion'] ?? true,
    enableFRSSIEuropeRegion: flags['enableFRSSIEuropeRegion'] ?? true,
    enableAuditMetricForFRS: flags['enableAuditMetricForFRS'] ?? true,
    enableAuditMetricForQSR: flags['enableAuditMetricForQSR'] ?? true,
    // Data is not currently available for Rebate/MPL/SPL widgets for the Institutional division in IMEA.
    // Defaults to false (shows "No Data Available"); flip to true via Azure App Configuration once data is available - no code change needed.
    // Once the bug-638057 resolved we can remove this flag and the related code.
    enableSpRebateMPLSPLWidgetsForIMEAInstitutional: flags['enableSpRebateMPLSPLWidgetsForIMEAInstitutional'] ?? false,
    enableRefreshedSolutionParticipationUI: flags['enableRefreshedSolutionParticipationUI'] ?? true,
  };
}

function removeSpinner() {
  const spinner = document.getElementById('initializer-spinner-container');
  if (spinner) {
    spinner.remove();
  }
}

function ensureSafeStartupUrl() {
  const pathname = window.location.pathname;
  if (pathname === '/') {
    return true;
  }

  // Construct redirect URL using only the origin (which cannot be spoofed)
  // and the safe root path. This prevents Open Redirect attacks by never
  // using untrusted location data in the redirect URL.
  const safeRedirectUrl = window.location.origin + '/';
  window.location.replace(safeRedirectUrl);
  return false;
}

function ensureSafeBrowserCache() {
  const version = packageJson.version;
  const browserCacheVersion = localStorage.getItem('browserCacheVersion');
  const sessionCacheVersion = sessionStorage.getItem('browserCacheVersion');

  // For some reason, the browser cache version in local storage gets cleared during the authentication handshakes (redirects).
  // Check the version in session storage as a fall back. Please note that the session storage is cleared when the browser tab is closed.
  if (!(browserCacheVersion == version || sessionCacheVersion == version)) {
    // Clear the local storage cache and session storage cache if the versions do not match the current version.
    localStorage.clear();
    // Clear session storage except for startUpParamDestination
    for (let index = 0; index < sessionStorage.length; index++) {
      let key = sessionStorage.key(index)
      if(key != null && key != 'startUpParamDestination'){
        sessionStorage.removeItem(key!);
      }
    }
  }

  // Set the new version in local storage and in session storage.
  localStorage.setItem('browserCacheVersion', version);
  sessionStorage.setItem('browserCacheVersion', version);
}

export type MixPanelConfig = {
  token: string,
  environment: string,
  sessionReplayCaptureRate: number
}

export type HostConfig = {
  ebrUrl: string,
  ebrApiUrl: string,
  programHubUrlHost: string,
  mixpanel: MixPanelConfig,
}

var hostConfig: HostConfig;

export const HOST_CONFIG = new InjectionToken('HostConfigToken');

function hostConfigFactory() {
  return hostConfig;
}

const hostConfigProvider: FactoryProvider = {
  provide: HOST_CONFIG,
  useFactory: hostConfigFactory
};

export type GlobalErrorHandlingConfig = {
  logErrorToConsole: boolean
}

var globalErrorHandlingConfig: GlobalErrorHandlingConfig;

export const GLOBAL_ERROR_HANDLING_CONFIG = new InjectionToken('GlobalErrorHandlingConfigToken');

function globalErrorHandlingConfigFactory() {
  return globalErrorHandlingConfig;
}

const globalErrorHandlingConfigProvider: FactoryProvider = {
  provide: GLOBAL_ERROR_HANDLING_CONFIG,
  useFactory: globalErrorHandlingConfigFactory
};

export type AzureMonitorConfig = {
  connectionString: string,
  diagnostics: {
    isDistributedTracingEnabled: boolean
  }
}

var azureMonitorConfig: AzureMonitorConfig;

export const AZURE_MONITOR_CONFIG = new InjectionToken('AzureMonitorConfigToken');

function azureMonitorConfigFactory() {
  return azureMonitorConfig;
}

const azureMonitorConfigProvider: FactoryProvider = {
  provide: AZURE_MONITOR_CONFIG,
  useFactory: azureMonitorConfigFactory
};

export type DownloadSettings = {
  maxPhotoSelection: number
}

var downloadSettings: DownloadSettings;

export const DOWNLOAD_SETTINGS = new InjectionToken('DownloadSettingsToken');

function downloadSettingsFactory() {
  return downloadSettings;
}

const downloadSettingsProvider: FactoryProvider = {
  provide: DOWNLOAD_SETTINGS,
  useFactory: downloadSettingsFactory
};

//#region Feature Flags

export type FeatureFlags = {
  enableTechnicalIssuesBanner: boolean,
  enableActionCenter: boolean,
  enableAsiaPacificRegionQSR: boolean,
  enableSolutionParticipationQsr: boolean,
  enableSolutionParticipationFrs: boolean,
  enableCostAssumptionsQSR: boolean,
  enableTvdBrandGroupTabs: boolean,
  enableAsiaPacificSolPartInstitutional: boolean,
  enableAsiaPacificSolPartFRS: boolean,
  enableAsiaPacificSolPartQSR: boolean,
  enableLatinAmericaSolPartInstitutional: boolean,
  enableLatinAmericaSolPartFRS: boolean,
  enableLatinAmericaSolPartQSR: boolean,
  enableTVDAndSPForIMEAInstitutional: boolean;
  enableTVDForAPInstitutional: boolean,
  enableTVDForLAInstitutional: boolean,
  enableCurrencyAndUoMConversion: boolean,
  enableFRSSIEuropeRegion: boolean,
  enableAuditMetricForFRS: boolean,
  enableAuditMetricForQSR: boolean,
  enableSpRebateMPLSPLWidgetsForIMEAInstitutional: boolean,
  enableRefreshedSolutionParticipationUI: boolean,
}

var featureFlags: FeatureFlags;

export const FEATURE_FLAGS = new InjectionToken('FeatureFlagsToken');

function featureFlagsFactory() {
  return featureFlags;
}

const featureFlagsProvider: FactoryProvider = {
  provide: FEATURE_FLAGS,
  useFactory: featureFlagsFactory
};
//#endregion

export const MIXPANEL_CONFIG = new InjectionToken('MixpanelConfigToken');

function mixpanelConfigFactory() {
  return hostConfig.mixpanel;
}

const mixpanelConfigProvider: FactoryProvider = {
  provide: MIXPANEL_CONFIG,
  useFactory: mixpanelConfigFactory
};

export const HOST_CONFIG_PROVIDERS = [
  hostConfigProvider,
  mixpanelConfigProvider,
  featureFlagsProvider,
  globalErrorHandlingConfigProvider,
  azureMonitorConfigProvider,
  downloadSettingsProvider
];

export type StartupParams = {
  source: string | null,
  destination: string | null
}

var startupParams: StartupParams;

async function getStartupParams() {
  const url = new URL(window.location.href);
  const source = url.searchParams.get('src');
  const destination = url.searchParams.get('dest');
  // If navigating from 1C, set the destination in session storage
  if(destination){
    sessionStorage.setItem('startUpParamDestination', destination);
  }

  // Set source & destination based on query params during initial app startup.
  // Upon app refresh, get destination from session storage
  startupParams = {
    source: source,
    destination: destination || sessionStorage.getItem('startUpParamDestination')
  };
}

export const STARTUP_PARAMS = new InjectionToken('StartupParamsToken');

function startupParamsFactory() {
  return startupParams;
}

export const APP_INITIALIZER_PROVIDERS = [
  MsalLoaderService,
  {
    provide: APP_INITIALIZER,
    useFactory: appInitializerFactory,
    deps: [ApiConfigurationService, MsalLoaderService, GlobalCacheLoaderService],
    multi: true
  },
  {
    provide: STARTUP_PARAMS,
    useFactory: startupParamsFactory
  }
];
